import { TimeRange } from '@mi-front/core';

/**
 * Calculates the time range for a list of segments.
 * Segments can be passed in any order and will be sorted internally (newest first, oldest last).
 * @param segments - Array of segments to calculate range for (can be in any order)
 * @param maxDurationMinutes - Optional maximum duration in minutes. If provided, the returned range
 *   will exceed this duration by exactly one segment (i.e., includes the oldest segment that makes
 *   the total duration exceed the limit)
 * @returns Object with `from` (start timestamp) and `to` (end timestamp) properties
 */
export function getRangeForSegments(
  segments: readonly { startTs: number; endTs: number }[],
  maxDurationMinutes?: number,
): TimeRange<number> {
  if (!segments.length) return { from: 0, to: 0 };

  // Sort newest first
  const sorted = [...segments].sort((a, b) => b.endTs - a.endTs);

  const to = sorted[0].endTs;
  const maxDurationMs = maxDurationMinutes ? maxDurationMinutes * 60 * 1000 : Infinity;

  let from = sorted[0].startTs;

  for (let i = 0; i < sorted.length; i++) {
    const segment = sorted[i];
    const currentDuration = to - segment.startTs;

    // Update 'from' to the current segment's start
    from = segment.startTs;

    // If this segment just pushed us over the limit, we stop here
    // and return this segment as the "one segment that exceeds the range."
    if (currentDuration > maxDurationMs) {
      break;
    }
  }

  return { from, to };
}
